import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@branditdev/ui'
import { useEffect, useState } from 'react'
import { Facebook, Image as ImageIcon, Instagram } from 'react-feather'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useAppSelector } from '../../../redux/storeConfig/store'
import {
  getSocialConnection,
  isPlatformConnected,
  platformChannels,
  platformRow,
  publishSocialPost,
  syncSocialConnection,
  type SocialChannelOption,
  type SocialPlatformType
} from '../../CustomerAccount/Dashboard/social/client'

type ConnectedAccount = {
  type: SocialPlatformType
  key: 'facebook' | 'instagram'
  label: string
  nickname?: string
  name?: string
  username?: string
  connected: boolean
  channels: SocialChannelOption[]
  selectedChannelId?: string
}

const displayTitle = (account: ConnectedAccount) => {
  const nickname = account.nickname?.trim()
  return nickname || account.label
}

const PLATFORM_OPTIONS: { type: SocialPlatformType; label: string; key: 'facebook' | 'instagram' }[] = [
  { type: 'FACEBOOK', label: 'Facebook', key: 'facebook' },
  { type: 'INSTAGRAM', label: 'Instagram', key: 'instagram' }
]

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  productId: number | string
  digitalProofingProductId: number | string
  previewUrl?: string | null
  defaultSocialCaption?: string | null
  onPublished?: () => void
}

export default function PublishSocialModal({
  open,
  onOpenChange,
  productId,
  digitalProofingProductId,
  previewUrl,
  defaultSocialCaption = '',
  onPublished
}: Props) {
  const currentLocation = useAppSelector(state => state.storeViews?.current)
  const currentLocationId = currentLocation?.id
  const fetchKey =
    open && currentLocationId !== undefined && currentLocationId !== null ? String(currentLocationId) : null
  const caption = (defaultSocialCaption || '').toString()
  const [accounts, setAccounts] = useState<ConnectedAccount[]>([])
  const [selected, setSelected] = useState<Record<SocialPlatformType, boolean>>({
    FACEBOOK: false,
    INSTAGRAM: false
  })
  const [channelIds, setChannelIds] = useState<Partial<Record<'facebook' | 'instagram', string>>>({})
  const [submitting, setSubmitting] = useState(false)
  const [trackedFetchKey, setTrackedFetchKey] = useState(fetchKey)
  const [resolvedFetchKey, setResolvedFetchKey] = useState<string | null>(null)

  if (fetchKey !== trackedFetchKey) {
    setTrackedFetchKey(fetchKey)
    setResolvedFetchKey(null)
  }

  const loadingAccounts = fetchKey !== null && resolvedFetchKey !== fetchKey

  useEffect(() => {
    if (fetchKey === null || currentLocationId === undefined || currentLocationId === null) return

    let cancelled = false

    const applyConnection = (data: Awaited<ReturnType<typeof syncSocialConnection>>) => {
      const nextAccounts = PLATFORM_OPTIONS.map(({ type, label, key }) => {
        const row = platformRow(data, key)
        const channels = platformChannels(data, key)
        return {
          type,
          key,
          label,
          nickname: row?.nickname?.trim() || undefined,
          name: row?.name || undefined,
          username: row?.username || undefined,
          connected: isPlatformConnected(data, key),
          channels,
          selectedChannelId: row?.selected_channel_id || channels[0]?.id
        }
      })
      setAccounts(nextAccounts)
      setSelected({
        FACEBOOK: isPlatformConnected(data, 'facebook'),
        INSTAGRAM: isPlatformConnected(data, 'instagram')
      })
      setChannelIds({
        facebook: nextAccounts.find(a => a.key === 'facebook')?.selectedChannelId,
        instagram: nextAccounts.find(a => a.key === 'instagram')?.selectedChannelId
      })
    }

    syncSocialConnection(currentLocationId)
      .then(data => {
        if (!cancelled) applyConnection(data)
      })
      .catch(async () => {
        try {
          const data = await getSocialConnection(currentLocationId)
          if (!cancelled) applyConnection(data)
        } catch {
          if (cancelled) return
          setAccounts(
            PLATFORM_OPTIONS.map(({ type, label, key }) => ({
              type,
              key,
              label,
              connected: false,
              channels: []
            }))
          )
          setSelected({ FACEBOOK: false, INSTAGRAM: false })
          setChannelIds({})
        }
      })
      .finally(() => {
        if (!cancelled) setResolvedFetchKey(fetchKey)
      })

    return () => {
      cancelled = true
    }
  }, [fetchKey, currentLocationId])

  const selectedPlatforms = PLATFORM_OPTIONS.filter(p => selected[p.type]).map(p => p.type)
  const anyConnected = accounts.some(account => account.connected)
  const selectedCount = selectedPlatforms.length

  const toggleAccount = (type: SocialPlatformType) => {
    setSelected(prev => ({ ...prev, [type]: !prev[type] }))
  }

  const onSubmit = async () => {
    if (!currentLocationId) {
      toast.error('No store view selected')
      return
    }
    if (!digitalProofingProductId) {
      toast.error('Proof is not ready to publish')
      return
    }
    if (!previewUrl) {
      toast.error('A proof image is required to publish')
      return
    }
    if (selectedPlatforms.length === 0) {
      toast.error('Select at least one connected account')
      return
    }

    const missingChannel = selectedPlatforms.find(type => {
      const key = type === 'FACEBOOK' ? 'facebook' : 'instagram'
      const account = accounts.find(a => a.key === key)
      return account && account.channels.length > 0 && !channelIds[key]
    })
    if (missingChannel) {
      toast.error(
        `Select a ${missingChannel === 'FACEBOOK' ? 'Page' : 'account'} for ${missingChannel === 'FACEBOOK' ? 'Facebook' : 'Instagram'}`
      )
      return
    }

    const payloadChannelIds = selectedPlatforms.reduce<Partial<Record<'facebook' | 'instagram', string>>>(
      (memo, type) => {
        const key = type === 'FACEBOOK' ? 'facebook' : 'instagram'
        if (channelIds[key]) memo[key] = channelIds[key]
        return memo
      },
      {}
    )

    setSubmitting(true)
    try {
      await publishSocialPost(currentLocationId, {
        product_id: productId,
        digital_proofing_product_id: digitalProofingProductId,
        platforms: selectedPlatforms,
        caption: caption.trim(),
        channel_ids: payloadChannelIds
      })
      onPublished?.()
      onOpenChange(false)
    } catch (error: any) {
      toast.error(error?.response?.data?.errors || 'Unable to publish to social')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={openState => !submitting && onOpenChange(openState)}>
      <DialogContent
        className="flex max-h-[min(90vh,52rem)] w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl"
        showCloseButton={!submitting}
      >
        <div className="shrink-0 border-b border-gray-200 px-6 pb-4 pr-14 pt-6">
          <DialogHeader>
            <DialogTitle>Create post</DialogTitle>
            <DialogDescription>
              Publish this proof for{' '}
              <span className="font-medium text-gray-900">{currentLocation?.name || 'this store view'}</span>
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4">
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <section className="rounded-2xl border border-gray-200 bg-gray-50/85 p-3.5 sm:p-4">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <p className="mb-0 text-sm font-semibold text-gray-900">Social accounts</p>
                  <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-gray-600 ring-1 ring-gray-200">
                    {selectedCount} selected
                  </span>
                </div>
                <p className="mb-3 text-sm text-gray-600">
                  Choose platforms for this post. If you have more than one Page or Instagram account, pick which one to
                  use below.
                </p>

                {loadingAccounts ? (
                  <div className="rounded-xl border border-dashed border-gray-300 bg-white/90 px-3.5 py-4 text-sm text-gray-600">
                    Refreshing Pages and accounts…
                  </div>
                ) : !anyConnected ? (
                  <div className="rounded-xl border border-dashed border-gray-300 bg-white/90 px-3.5 py-4 text-sm text-gray-600">
                    No accounts connected.{' '}
                    <Link
                      to="/customer/account/social"
                      onClick={() => onOpenChange(false)}
                      className="font-medium text-gray-900 underline underline-offset-2 hover:text-gray-700"
                    >
                      Connect Facebook / Instagram
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {accounts.map(account => {
                      const isSelected = !!selected[account.type] && account.connected
                      const PlatformIcon = account.type === 'FACEBOOK' ? Facebook : Instagram
                      const canToggle = account.connected && !submitting && !loadingAccounts
                      const channelNoun = account.key === 'instagram' ? 'account' : 'Page'
                      const showChannelPicker = isSelected && account.channels.length > 0

                      return (
                        <div
                          key={account.type}
                          className={`rounded-xl border p-3.5 transition-colors ${
                            !account.connected
                              ? 'border-gray-200 bg-white/70 opacity-60'
                              : isSelected
                                ? 'border-blue-200 bg-blue-50/60 ring-1 ring-blue-200'
                                : 'border-gray-200 bg-white'
                          }`}
                          style={isSelected ? { borderColor: 'var(--mf-primary-color, #012979)' } : undefined}
                        >
                          <div
                            role="button"
                            tabIndex={canToggle ? 0 : -1}
                            aria-disabled={!canToggle}
                            aria-pressed={isSelected}
                            onClick={() => canToggle && toggleAccount(account.type)}
                            onKeyDown={event => {
                              if (!canToggle) return
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault()
                                toggleAccount(account.type)
                              }
                            }}
                            className={`flex w-full min-w-0 items-center gap-3 text-left ${
                              canToggle ? 'cursor-pointer' : 'cursor-not-allowed'
                            }`}
                          >
                            <span
                              className={`inline-flex size-9 shrink-0 items-center justify-center rounded-full text-white ${
                                account.type === 'FACEBOOK' ? 'bg-[#1877F2]' : 'bg-[#E1306C]'
                              }`}
                            >
                              <PlatformIcon className="size-4" aria-hidden />
                            </span>
                            <div className="flex min-w-0 flex-1 items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="mb-0 truncate text-sm font-semibold text-gray-900">
                                  {displayTitle(account)}
                                </p>
                                <p className="mb-0 truncate text-sm text-gray-600">
                                  {account.connected
                                    ? account.username
                                      ? `@${account.username.replace(/^@/, '')}`
                                      : account.name || 'Connected'
                                    : 'Not connected'}
                                </p>
                              </div>
                              {isSelected && (
                                <span
                                  className="shrink-0 rounded-full bg-white px-2 py-0.5 text-xs font-medium"
                                  style={{
                                    color: 'var(--mf-primary-color, #012979)',
                                    boxShadow: 'inset 0 0 0 1px var(--mf-primary-color, #012979)'
                                  }}
                                >
                                  Selected
                                </span>
                              )}
                            </div>
                          </div>

                          {showChannelPicker ? (
                            <div
                              className="mt-3"
                              onClick={event => event.stopPropagation()}
                              onKeyDown={event => event.stopPropagation()}
                            >
                              <label
                                htmlFor={`publish-channel-${account.key}`}
                                className="mb-1.5 block text-xs font-medium text-gray-700"
                              >
                                {account.channels.length > 1
                                  ? `Choose ${channelNoun} for this post`
                                  : `Publishing to this ${channelNoun}`}
                              </label>
                              <Select
                                value={channelIds[account.key] || undefined}
                                disabled={submitting || loadingAccounts || account.channels.length === 1}
                                onValueChange={value =>
                                  setChannelIds(prev => ({
                                    ...prev,
                                    [account.key]: value
                                  }))
                                }
                              >
                                <SelectTrigger id={`publish-channel-${account.key}`} className="w-full bg-white">
                                  <SelectValue placeholder={`Select a ${channelNoun}`} />
                                </SelectTrigger>
                                <SelectContent>
                                  {account.channels.map(channel => (
                                    <SelectItem key={channel.id} value={channel.id}>
                                      {channel.name || channel.id}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          ) : null}
                        </div>
                      )
                    })}
                  </div>
                )}
              </section>

              <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white/95 p-3.5 shadow-sm sm:p-4">
                <div className="mb-1.5 flex items-center gap-2">
                  <ImageIcon className="size-4 text-gray-400" aria-hidden />
                  <p className="mb-0 text-sm font-semibold text-gray-900">Media</p>
                </div>
                <p className="mb-3 text-sm text-gray-600">
                  This approved digital proof will be attached automatically. Media cannot be changed here.
                </p>
                {previewUrl ? (
                  <div className="flex min-h-40 items-center justify-center rounded-xl border border-gray-200 bg-gray-50/85 p-3">
                    <img src={previewUrl} alt="Proof to publish" className="max-h-48 max-w-full object-contain" />
                  </div>
                ) : (
                  <p className="mb-0 text-sm text-red-600">Proof image is missing. Generate a PNG/JPEG proof first.</p>
                )}
              </section>
            </div>

            <section className="space-y-4 rounded-2xl border border-gray-200 bg-white/95 p-3.5 shadow-sm sm:p-4">
              <p className="mb-0 text-sm font-semibold text-gray-900">Post Caption</p>
              <p className="mb-0 text-xs text-gray-500">
                Caption is set by your administrator for this social product.
              </p>
              <textarea
                id="publish-social-caption"
                rows={4}
                value={caption}
                readOnly
                className="min-h-[88px] w-full cursor-default rounded-md border border-gray-300 bg-gray-200 px-3 py-2 text-base text-gray-600 shadow-sm md:text-sm"
              />
            </section>
          </div>
        </div>

        <div className="shrink-0 border-t border-gray-200 bg-white px-6 py-4">
          <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:gap-0">
            <Button
              type="button"
              variant="outline"
              disabled={submitting}
              onClick={() => onOpenChange(false)}
              className="h-10 w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={submitting || loadingAccounts || !anyConnected || !previewUrl}
              onClick={onSubmit}
              className="h-10 w-full sm:w-auto"
            >
              {submitting ? 'Publishing…' : loadingAccounts ? 'Refreshing…' : 'Publish'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}
